import React, { useState, useEffect } from "react";
import { Form, Input, Button, Card, Row, Col, Typography, InputNumber, Alert, Select, Upload, message, Modal, Table } from "antd";
import { UploadOutlined } from '@ant-design/icons';
import { baseurl } from "../../../components/lib/settings";
import "./createm.css"

const { Title } = Typography;
const { Option } = Select;

const CreateMusician = () => {
  const [editions, setEditions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [erroMessage, setErrorMessage] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [fileList, setFileList] = useState([]);
  const [selectedEditionUp, setSelectedEditionUp] = useState(null);
  const [selectedEditionObj, setSelectedEditionObj] = useState(null);
  const [prefix, setPrefix] = useState('');
  const [form] = Form.useForm();
  const [parsedData, setParsedData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // Carrega as edições ativas com suas companies
  useEffect(() => {
    baseurl.get('v3/get_available_editions/')
      .then(response => {
        setEditions(response.data);
      })
      .catch(error => {
        console.error("Erro ao carregar edições:", error);
      });
  }, []);

  const beforeUpload = (file) => {
    const isDocx = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    if (!isDocx) {
      message.error('Apenas arquivos DOCX são permitidos!');
    }
    return isDocx;
  };

  const handleUpload = ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('file', file);

    baseurl.post('v3/parse_musician_docx/', formData)
      .then(response => {
        onSuccess(response.data, file);
        
        if (response.data && response.data.length > 0) {
          setParsedData(response.data.map(item => ({
            ...item,
            voting_code: `${prefix}${item.song_number}`
          })));
          
          if (response.data.length > 1) {
            setIsModalVisible(true);
          } else {
            const firstItem = response.data[0];
            form.setFieldsValue({
              song_number: firstItem.song_number,
              author: firstItem.author,
              title: firstItem.title,
              contact: firstItem.contact,
              wpp_contact: firstItem.whatsapp_number || '',
              voting_code: `${prefix}${firstItem.song_number}`
            });
          }
        }
      })
      .catch(error => {
        onError(error);
        setAlertType("error");
        setErrorMessage("Erro ao processar arquivo. Verifique o formato.");
      });
  };

  const handleBulkSubmit = () => {
    setIsLoading(true);
    const promises = parsedData.map(item => {
      const data = {
        edicao: selectedEditionUp,
        song_number: item.song_number,
        author: item.author,
        title: item.title,
        contact: item.contact,
        wpp_contact: item.whatsapp_number || '',
        voting_code: item.voting_code,
        // company: selectedEditionObj?.company?.id, // Usa a company da edição
        available_to_vote: true,
        request_source: "web_platform"
      };

      return baseurl.post('v1/manage_musician/', data);
    });

    Promise.all(promises)
      .then(responses => {
        setIsModalVisible(false);
        setAlertType("success");
        setErrorMessage(`${responses.length} músicos registrados com sucesso!`);
        form.resetFields();
        setFileList([]);
      })
      .catch(error => {
        setAlertType("error");
        setErrorMessage("Erro ao registrar alguns músicos");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onFinish = (values) => {
    setIsLoading(true);
    
    const data = {
      edicao: selectedEditionUp,
      song_number: values.song_number,
      author: values.author,
      title: values.title,
      contact: values.contact,
      whatsapp_number: values.wpp_contact,
      voting_code: `${prefix}${values.song_number}`,
      // company: selectedEditionObj?.company?.id, // Usa a company da edição
      available_to_vote: true,
      request_source: "web_platform"
    };

    baseurl.post('v1/manage_musician/', data)
      .then((resp) => {
        if (resp.status === 200 || resp.status === 201) {
          form.resetFields();
          setAlertType("success");
          setErrorMessage("Músico registrado com sucesso!");
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setAlertType("error");
        setErrorMessage(error.response?.data?.message || "Erro ao registrar músico");
      });
  };

  // Atualizado para armazenar o objeto completo da edição
  const handleEditionChange = (value) => {
    const edition = editions.find(e => e.id === value);
    setSelectedEditionUp(value);
    setSelectedEditionObj(edition);
  };

  const columns = [
    { title: 'Número', dataIndex: 'song_number', key: 'song_number' },
    { title: 'Autor', dataIndex: 'author', key: 'author' },
    { title: 'Título', dataIndex: 'title', key: 'title' },
    { title: 'Contacto', dataIndex: 'contact', key: 'contact' },
    { title: 'WhatsApp', dataIndex: 'whatsapp_number', key: 'whatsapp_number' },
    { title: 'Código', dataIndex: 'voting_code', key: 'voting_code' },
  ];

  return (
    <Row justify="center" align="middle" style={{ minHeight: "50vh", backgroundColor: "#eeeeee" }}>
      <Col span={20}>
        <Card>
          <Title level={2} style={{ textAlign: "center" }}>
            Registo de Canções para Votação
          </Title>
          
          <div className="messages-notif">
            {erroMessage && <Alert style={{ width: "100%" }} message={erroMessage} type={alertType} showIcon />}
          </div>

          <Form
            form={form}
            name="musician_registration"
            onFinish={onFinish}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 14 }}
          >
            <Form.Item
              label="Edição"
              name="edition"
              rules={[{ required: true, message: "Por favor, selecione a edição!" }]}
            >
              <Select 
                placeholder="Selecione a edição"
                onChange={handleEditionChange}
                disabled={isLoading}
              >
                {editions.map(edition => (
                  <Option key={edition.id} value={edition.id}>
                    {edition.description} ({edition.delegacao?.description || 'Sem empresa'})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Prefixo do Código"
              name="prefix"
              rules={[{ required: true, message: "Por favor, insira o prefixo!" }]}
            >
              <Input 
                placeholder="Ex: NG23-"
                onChange={(e) => setPrefix(e.target.value)}
                disabled={isLoading}
              />
            </Form.Item>

            <Form.Item
              label="Importar de Arquivo"
              name="document"
            >
              <Upload
                fileList={fileList}
                beforeUpload={beforeUpload}
                customRequest={handleUpload}
                onChange={({ fileList }) => setFileList(fileList)}
                accept=".docx"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />} disabled={isLoading || !selectedEditionUp || !prefix}>
                  Selecionar Arquivo DOCX
                </Button>
              </Upload>
            </Form.Item>

            {/* {!isModalVisible && (
              <>
                <Form.Item
                  label="Número da canção"
                  name="song_number"
                  rules={[{ required: true, message: "Por favor, insira o número da canção!" }]}
                >
                  <InputNumber disabled={isLoading} min={1} style={{ width: '100%' }} size="large" />
                </Form.Item>


                <Form.Item wrapperCol={{ offset: 6 }}>
                  <Button type="primary" htmlType="submit" loading={isLoading}>
                    Registrar novo músico
                  </Button>
                </Form.Item>
              </>
            )} */}
          </Form>
        </Card>
      </Col>

      <Modal
        title="Confirmar Registro de Músicos"
        visible={isModalVisible}
        width={1000}
        onOk={handleBulkSubmit}
        onCancel={() => setIsModalVisible(false)}
        okText="Confirmar Registro"
        cancelText="Cancelar"
        confirmLoading={isLoading}
      >
        <p>Foram encontrados {parsedData.length} registros no arquivo. Confirme os dados antes de prosseguir:</p>
        <Table 
          columns={columns} 
          dataSource={parsedData} 
          size="small" 
          pagination={{ pageSize: 10 }}
          rowKey={(record) => `${record.song_number}-${record.author}`}
        />
      </Modal>
    </Row>
  );
};

export default CreateMusician;
